import { useCallback, useEffect, useRef, useState } from "react";
import { GlobalStyleState, ProductState } from "../store/chart";
import debounce from "lodash/debounce";
import {
  DesktopTableBody,
  DesktopTableColumn,
  DesktopTableRow,
  DesktopTableStickyColumn,
} from "./Table";
import styled from "styled-components";
import { Text } from "./Text";
import { FontWeight, UIColor, UIText } from "../types";
import { ComparisonPlaceholder } from "./Comparison";
import { trackShopSelectClick } from "../analytics";
import { ShopButton } from "./Product";

type DesktopShopSelectSectionProps = {
  className?: string;
  selectedProducts: ProductState[];
  styling: GlobalStyleState;
  backgroundColor?: UIColor;
  title: UIText;
  description: UIText;
  openOnLoad: boolean;
};

export function DesktopShopSelectSection({
  selectedProducts,
  styling,
  backgroundColor,
  title,
  description,
  openOnLoad,
}: DesktopShopSelectSectionProps) {
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const handleResize = useCallback(() => {
    if (bodyRef.current) {
      const children = Array.from(bodyRef.current.children);
      const height = children.reduce(
        (acc, child) => acc + child.clientHeight,
        0,
      );
      setMaxHeight(height + 100);
    }
  }, []);

  const handleCollapseToggle = useCallback(() => {
    handleResize();
    setCollapsed((collapsed) => !collapsed);
  }, [handleResize]);

  useEffect(() => {
    window.addEventListener("resize", debounce(handleResize, 300));
    window.addEventListener("deviceorientation", debounce(handleResize, 300));
    // handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("deviceorientation", handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    setCollapsed(!openOnLoad);
  }, [openOnLoad]);

  return (
    <>
      <DesktopTableBody>
        <DesktopTableRow fluid={selectedProducts.length === 0}>
          <DesktopTableStickyColumn backgroundColor={backgroundColor?.hex}>
            <DesktopFeatureSectionHeader onClick={handleCollapseToggle}>
              <CollapsibleIcon collapsed={collapsed} />
              <DesktopFeatureSectionTitle
                font={title.font}
                textColor={title.color}
              >
                {title.value}
              </DesktopFeatureSectionTitle>
            </DesktopFeatureSectionHeader>
          </DesktopTableStickyColumn>
          {selectedProducts.map((product) => (
            <td key={product.id} />
          ))}
        </DesktopTableRow>
      </DesktopTableBody>
      <DesktopFeatureSectionBody
        ref={bodyRef}
        collapsed={collapsed}
        style={
          collapsed ? { maxHeight: 0 } : maxHeight !== null ? { maxHeight } : {}
        }
      >
        <DesktopTableRow fluid={selectedProducts.length === 0}>
          <DesktopTableStickyColumn backgroundColor={backgroundColor?.hex}>
            <DesktopFeatureColumnHeader>
              <DesktopShopSelectDescription
                font={description.font}
                textColor={description.color}
              >
                {description.value}
              </DesktopShopSelectDescription>
            </DesktopFeatureColumnHeader>
          </DesktopTableStickyColumn>
          {selectedProducts.map((product) => (
            <DesktopFeatureColumn key={product.id}>
              <ComparisonPlaceholder product={product} styling={styling}>
                {product.showShopSelectButton && product.shopSelectButton && (
                  <ShopSelectContainer>
                    <ShopButton
                      as="a"
                      target="_blank"
                      href={product.shopSelectButton.url}
                      onClick={() => trackShopSelectClick(product)}
                      textColor={styling.shopSelectButton.textColor}
                      backgroundColor={styling.shopSelectButton.backgroundColor}
                      borderColor={styling.shopSelectButton.borderColor}
                      hover={styling.shopSelectButton.hover}
                    >
                      {product.shopSelectButton.buttonCopy}
                    </ShopButton>
                  </ShopSelectContainer>
                )}
              </ComparisonPlaceholder>
            </DesktopFeatureColumn>
          ))}
        </DesktopTableRow>
      </DesktopFeatureSectionBody>
    </>
  );
}

const DesktopFeatureSectionHeader = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.getValue(16)}px;
  cursor: pointer;
  padding: ${({ theme }) =>
    `${theme.getValue(30)}px ${theme.getValue(16)}px ${theme.getValue(
      10,
    )}px 0`};
`;

const DesktopFeatureSectionBody = styled.tbody<{ collapsed?: boolean }>`
  display: block;
  opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
  transition: all 0.5s;
`;

const DesktopFeatureSectionTitle = styled(Text)`
  &&& {
    font-size: ${({ theme }) => theme.getFontSize(36)}px;
    line-height: 1;
    margin: 0;
  }
`;

const DesktopShopSelectDescription = styled(Text)`
  &&& {
    font-weight: ${({ theme }) =>
      theme.isMobile ? FontWeight.Normal : FontWeight.SemiBold};
    font-size: ${({ theme }) =>
      theme.isMobile ? theme.getFontSize(16) : theme.getFontSize(22)}px;
    line-height: 1;
    margin: 0;
    text-align: ${({ theme }) => (theme.isMobile ? "center" : "left")};

    sup {
      font-size: ${({ theme }) =>
        theme.isMobile ? theme.getFontSize(10) : theme.getFontSize(12)}px;
    }
  }
`;

const CollapsibleIcon = styled.div<{ collapsed?: boolean }>`
  position: relative;
  top: -${({ theme }) => theme.getValue(3)}px;
  font-size: ${({ theme }) => theme.getFontSize(28)}px;
  line-height: 1;
  width: ${({ theme }) => theme.getValue(28)}px;
  height: ${({ theme }) => theme.getValue(28)}px;
  border: 0.07em solid ${({ theme }) => theme.colors.black};
  border-radius: 50%;

  &:before {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0.07em;
    height: 0.5em;
    content: "";
    transform: ${({ collapsed }) =>
      collapsed
        ? "translate(-50%, -50%)"
        : "translate(-50%, -50%) rotate(90deg)"};
    background: ${({ theme }) => theme.colors.black};
    opacity: ${({ collapsed }) => (collapsed ? 1 : 0)};
    transition: all 0.5s;
  }

  &:after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0.5em;
    height: 0.07em;
    content: "";
    transform: translate(-50%, -50%);
    background: ${({ theme }) => theme.colors.black};
  }
`;

const DesktopFeatureColumnHeader = styled.div`
  z-index: 20;
  height: 100%;
  margin-left: ${({ theme }) => `${theme.getValue(44)}px`};
  padding: ${({ theme }) =>
    `${theme.getValue(32)}px ${theme.getValue(16)}px ${theme.getValue(
      32,
    )}px 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.black};
`;

const DesktopFeatureColumn = styled(DesktopTableColumn)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.black};
`;

const ShopSelectContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: ${({ theme }) => `${theme.getValue(32)}px ${theme.getValue(16)}px`};
`;
